import { useCallback, useEffect, useState } from "react";
import { Ban } from "lucide-react";
import { Avatar, Button, Dialog, toast } from "../ui";
import { discussionsApi, errMessage } from "./api";

/**
 * Manage the members you've blocked (Google Play UGC "block users"). Lists your
 * blocks and lets you unblock. Pairs with the "Block member" action in the
 * message menu (MessageBubble / TopicThreadPage).
 */
export default function BlockedMembersDialog({ open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [blocks, setBlocks] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    discussionsApi
      .listBlocks()
      .then((r) => setBlocks(r.blocks || []))
      .catch((e) => toast.error(errMessage(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const unblock = async (item) => {
    const uid = item.user && item.user.id;
    if (!uid || busy) return;
    setBusy(uid);
    try {
      await discussionsApi.unblockUser(uid);
      setBlocks((prev) => prev.filter((b) => (b.user && b.user.id) !== uid));
      toast.success(`Unblocked ${(item.user && item.user.name) || "member"}`);
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Blocked members"
      className="vdsc-blocked"
      footer={
        <Button variant="secondary" onClick={() => onOpenChange(false)}>
          Done
        </Button>
      }
    >
      {loading ? (
        <p className="vdsc-sub">Loading…</p>
      ) : blocks.length === 0 ? (
        <p className="vdsc-sub">
          You haven’t blocked anyone. Blocking a member hides their messages
          from you across the community.
        </p>
      ) : (
        <ul className="vdsc-blocked-list">
          {blocks.map((b) => (
            <li key={(b.user && b.user.id) || (b.user && b.user.name)} className="vdsc-blocked-row">
              <Avatar
                name={b.user && b.user.name}
                imageUrl={b.user && b.user.avatar_url}
                size={32}
              />
              <span className="vdsc-blocked-name">
                {(b.user && b.user.name) || "Member"}
              </span>
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Ban size={13} />}
                loading={busy === (b.user && b.user.id)}
                onClick={() => unblock(b)}
              >
                Unblock
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}
